import { useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowUpRight, Check, ChevronDown, ChevronLeft, ChevronRight, Settings2 } from "lucide-react";
import { Button as AriaButton, Dialog, DialogTrigger, Popover } from "react-aria-components";
import { cn } from "@/utils/helpers";
import type { CalendarPlatform } from "@/types/contentCalendar.type";
import { KitSegmentedControl } from "../../components/kit/SegmentedControl";
import PlatformGlyph from "../../components/PlatformGlyph";
import { useCoShellPortal } from "../../CoShellPortalContext";
import {
  CHANNEL_NAME,
  MONTH_NAMES,
  pillarHue,
  type CalendarFilters,
  type FilterCounts,
} from "./calendarModel";
import { CalendarTip } from "./CalendarParts";
import "./calendar.css";

export type CalendarViewMode = "month" | "week" | "list" | "schedule";

const VIEW_OPTIONS: { label: string; value: CalendarViewMode; title: string }[] = [
  { label: "Month", value: "month", title: "Month (M)" },
  { label: "Week", value: "week", title: "Week (W)" },
  { label: "List", value: "list", title: "List (L)" },
  { label: "Next 14 days", value: "schedule", title: "Next 14 days (S)" },
];

export interface MonthPickerData {
  year: number;
  /** 1-12, the month that is open. */
  month: number;
  /** False where no month is open (the year page), so none is marked. */
  markOpen?: boolean;
  /** "2026-12" for each month that has posts. */
  monthsWithPosts: ReadonlySet<string>;
  onPick: (year: number, month: number) => void;
  onSeeYear?: () => void;
  /** Each month's state line ("2 need you", "Not planned", "Plans itself on 5 Jan"). */
  monthLine?: (year: number, month: number) => MonthLine | null;
}

export interface MonthLine {
  text: string;
  tone: "accent" | "alert" | "muted";
}

export interface HeaderFilters {
  counts: FilterCounts;
  value: CalendarFilters;
  onChange: (next: CalendarFilters) => void;
  /** "With Fatafeat", or "With the team" for the client. */
  otherLabel: string;
  /** Heads the pillar menu's mix ("December by pillar"). */
  monthName?: string;
  /** A closed month filters on Missed in place of Needs you and With the client. */
  closed?: boolean;
}

/** A 32px control-glass circle (Today's arrows, settings). */
export const glassCircle =
  "co-glass-control grid size-8 shrink-0 place-items-center rounded-pill text-co-text2 outline-none transition-colors hover:text-co-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent";
/** A 32px control-glass capsule (Today). */
export const glassCapsule =
  "co-glass-control inline-flex h-8 shrink-0 items-center rounded-pill px-3 text-[13px] font-medium text-co-text outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent";

/**
 * CalendarHeader (library Calendar / Header, style B): the title with its month
 * picker, Today, the arrows, the Month, Week and List switch and settings, all
 * in control glass; then the status line and the filters. Once the grid scrolls
 * under it, Calendar / Header · Scrolled, one 56px row of bar frost, takes over.
 */
export default function CalendarHeader({
  title,
  compactTitle,
  view,
  onViewChange,
  onToday,
  onPrev,
  onNext,
  prevLabel,
  nextLabel,
  picker,
  status,
  statusTone = "default",
  filters,
  settings,
  layout = "desktop",
  views = true,
  weekLabel = "Week",
  arrows = true,
  filterRow,
  dense = false,
}: {
  /** The year: a tighter header so twelve months fit the screen. */
  dense?: boolean;
  /** Schedule runs from today, so it has no arrows. */
  arrows?: boolean;
  /** A view's own filters (Schedule's Needs you, Scheduled, Channels) in place of the month's. */
  filterRow?: ReactNode;
  /** "3 days" on a tablet, whose week shows three days at a time. */
  weekLabel?: string;
  /** The year has no Month, Week and List switch. */
  views?: boolean;
  /**
   * "tablet" stacks the header (DESIGN.md "The calendar on a tablet"): the title
   * with Today and the arrows, then the view switch with the filters, then the
   * status line. Settings live in the compact shell's top lane there.
   */
  layout?: "desktop" | "tablet";
  title: string;
  /** The scrolled row's shorter title. */
  compactTitle?: string;
  view: CalendarViewMode;
  onViewChange: (view: CalendarViewMode) => void;
  /** Absent where the window never moves (the year). */
  onToday?: () => void;
  onPrev: () => void;
  onNext: () => void;
  prevLabel: string;
  nextLabel: string;
  picker?: MonthPickerData | null;
  status: ReactNode;
  statusTone?: "default" | "accent" | "alert" | "muted";
  filters?: HeaderFilters | null;
  settings?: { label: string; onPress: () => void }[];
}) {
  const sentinel = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const compactTitleRef = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const node = sentinel.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => {
      setScrolled(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const statusColor =
    statusTone === "accent"
      ? "var(--co-accent-ink)"
      : statusTone === "alert"
        ? "var(--co-alert-ink)"
        : statusTone === "muted"
          ? "var(--co-muted)"
          : "var(--co-text2)";

  const navigation = (
    <>
      {onToday ? (
        <CalendarTip label="Today" keys={["T"]}>
          <AriaButton onPress={onToday} className={glassCapsule}>
            Today
          </AriaButton>
        </CalendarTip>
      ) : null}
      {arrows ? (
        <>
          <CalendarTip label={prevLabel} keys={["←"]}>
            <AriaButton aria-label={prevLabel} onPress={onPrev} className={glassCircle}>
              <ChevronLeft className="size-4" aria-hidden="true" />
            </AriaButton>
          </CalendarTip>
          <CalendarTip label={nextLabel} keys={["→"]}>
            <AriaButton aria-label={nextLabel} onPress={onNext} className={glassCircle}>
              <ChevronRight className="size-4" aria-hidden="true" />
            </AriaButton>
          </CalendarTip>
        </>
      ) : null}
    </>
  );

  const viewSwitch = !views ? null : (
    <KitSegmentedControl
      variant="lens"
      ariaLabel="Calendar view"
      options={VIEW_OPTIONS.map((option) => (option.value === "week" ? { ...option, label: weekLabel } : option))}
      value={view}
      onChange={onViewChange}
    />
  );

  const settingsMenu = settings && settings.length > 0 ? <SettingsMenu items={settings} /> : null;

  return (
    <>
      {/* The scrolled row: sticky at the top of the page's scroller, zero height
          until the month passes under it. */}
      <div className="sticky top-0 z-30 h-0">
        <div
          aria-hidden={!scrolled}
          inert={!scrolled}
          className={cn(
            "co-cal-glass absolute inset-x-0 top-0 flex h-14 items-center gap-3 px-8 max-lg:px-3",
            "transition-opacity duration-[var(--duration-short)] ease-[var(--ease-out-expo)]",
            scrolled ? "opacity-100" : "pointer-events-none opacity-0",
          )}
        >
          <div ref={compactTitleRef} className="flex min-w-0 items-center gap-1.5">
            <span className="truncate font-display text-[26px] font-bold leading-[30px] tracking-normal text-co-text">
              {compactTitle ?? title}
            </span>
            {picker ? <MonthPicker picker={picker} anchorRef={compactTitleRef} compact /> : null}
          </div>
          {filters ? (
            <FilterButton
              label="Needs you"
              count={filters.counts.needsYou}
              active={filters.value.needsYou}
              onPress={() => filters.onChange({ ...filters.value, needsYou: !filters.value.needsYou })}
            />
          ) : null}
          <span className="flex-1" />
          <span className="flex items-center gap-3">
            {navigation}
            {viewSwitch}
            {settingsMenu}
          </span>
        </div>
      </div>

      {layout === "tablet" ? (
        // Calendar / Tablet header: pad 16/20/14, gap 10; Crake 38, 36px glass controls.
        <header className="flex flex-col gap-2.5 px-2 pb-3.5 pt-4">
          <div className="flex items-center gap-2">
            <div ref={titleRef} className="flex min-w-0 items-center gap-2">
              <h1 className="min-w-0 truncate font-display text-[38px] font-bold leading-[1.14] tracking-normal text-co-text">
                {title}
              </h1>
              {picker ? <MonthPicker picker={picker} anchorRef={titleRef} tablet /> : null}
            </div>
            <span className="flex-1" />
            {onToday ? (
              <CalendarTip label="Today" keys={["T"]}>
                <AriaButton onPress={onToday} className={cn(glassCapsule, "h-9 px-3.5 text-[14px]")}>
                  Today
                </AriaButton>
              </CalendarTip>
            ) : null}
            {arrows ? (
              <>
                <AriaButton aria-label={prevLabel} onPress={onPrev} className={cn(glassCircle, "size-9")}>
                  <ChevronLeft className="size-[18px]" aria-hidden="true" />
                </AriaButton>
                <AriaButton aria-label={nextLabel} onPress={onNext} className={cn(glassCircle, "size-9")}>
                  <ChevronRight className="size-[18px]" aria-hidden="true" />
                </AriaButton>
              </>
            ) : null}
          </div>
          <div ref={sentinel} aria-hidden="true" className="h-0" />
          <div className="-mt-2.5 flex flex-wrap items-center justify-between gap-2">
            {viewSwitch}
            {filterRow ?? (filters ? <FilterRow filters={filters} className="gap-2" /> : null)}
          </div>
          <p role="status" aria-live="polite" className="min-w-0 text-[15px] leading-5" style={{ color: statusColor }}>
            {status}
          </p>
        </header>
      ) : (
        <header className={cn("flex flex-col gap-3 px-8", dense ? "pb-2 pt-4" : "pb-4 pt-[26px]")}>
          {/* At 730 wide (both islands open at 1440) the controls drop under the title instead of
              squeezing it to "October…" and wrapping "Next 14 days". */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
            <div ref={titleRef} className="flex min-w-0 items-center gap-2.5">
              <h1 className="min-w-0 truncate font-display text-[50px] font-normal leading-[70px] tracking-normal text-co-text">
                {title}
              </h1>
              {picker ? <MonthPicker picker={picker} anchorRef={titleRef} /> : null}
            </div>
            <span className="flex-1" />
            {navigation}
            {viewSwitch ? <span aria-hidden="true" className="w-2 shrink-0" /> : null}
            {viewSwitch}
            {settingsMenu}
          </div>
          <div ref={sentinel} aria-hidden="true" className="h-0" />
          <div className={cn("-mt-3 flex items-center gap-2.5", (filters || filterRow) && "min-h-8")}>
            <p role="status" aria-live="polite" className="min-w-0 text-[15px] leading-5" style={{ color: statusColor }}>
              {status}
            </p>
            <span className="flex-1" />
            {filterRow ?? (filters ? <FilterRow filters={filters} /> : null)}
          </div>
        </header>
      )}
    </>
  );
}

export function FilterButton({
  label,
  count,
  active,
  onPress,
  menu = false,
}: {
  label: string;
  count?: number;
  active: boolean;
  onPress?: () => void;
  menu?: boolean;
}) {
  return (
    <AriaButton
      onPress={onPress}
      aria-pressed={menu ? undefined : active}
      className={cn(
        // Filter / Calendar: a 32px control-glass capsule; on, the accent tint
        // with its border and a leading check (Filter / Calendar · On).
        "co-glass-control inline-flex h-8 shrink-0 items-center gap-1.5 rounded-pill px-3 text-[13px] outline-none transition-colors",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent",
        active
          ? "!bg-co-accent-dim font-semibold text-co-text !shadow-[inset_0_0_0_1px_var(--co-accent-border)]"
          : "font-medium text-co-text2 hover:text-co-text",
      )}
    >
      {active ? <Check className="size-3.5 text-co-accent" strokeWidth={2.25} aria-hidden="true" /> : null}
      {label}
      {count !== undefined ? (
        <span className={cn("text-[12px] font-normal tabular-nums", active ? "text-co-accent" : "text-co-muted")}>
          {count}
        </span>
      ) : null}
      {menu && !active ? <ChevronDown className="size-3.5 text-co-muted" aria-hidden="true" /> : null}
    </AriaButton>
  );
}

/** The floating chrome a calendar menu opens in (Menu, Calendar / Pillar menu): radius 12, pad 6. */
export const menuPanel = "co-glass-chrome relative rounded-card p-1.5 outline-none";

/**
 * The filters as control-glass capsules; the phone's filter chips
 * (PhoneFilterChip) are the same buttons in a row that scrolls sideways.
 */
export function FilterRow({ filters, className }: { filters: HeaderFilters; className?: string }) {
  const { counts, value, onChange, otherLabel, monthName, closed } = filters;
  const portal = useCoShellPortal();
  const channelName = value.channel ? CHANNEL_NAME[value.channel] : null;
  const pillar = counts.pillars.find((entry) => entry.id === value.pillarId);
  const total = counts.pillars.reduce((sum, entry) => sum + entry.count, 0);
  return (
    <div className={cn("flex flex-wrap items-center gap-2.5", className)}>
      {closed ? (
        <FilterButton
          label="Missed"
          count={counts.missed}
          active={!!value.missed}
          onPress={() => onChange({ ...value, missed: !value.missed })}
        />
      ) : (
        <>
          <FilterButton
            label="Needs you"
            count={counts.needsYou}
            active={value.needsYou}
            onPress={() => onChange({ ...value, needsYou: !value.needsYou })}
          />
          <FilterButton
            label={otherLabel}
            count={counts.withClient}
            active={value.withClient}
            onPress={() => onChange({ ...value, withClient: !value.withClient })}
          />
        </>
      )}
      <DialogTrigger>
        <FilterButton label={channelName ?? "Channels"} active={!!value.channel} menu />
        <Popover
          placement="bottom end"
          offset={6}
          UNSTABLE_portalContainer={portal ?? undefined}
          className="co-cal-peek z-[var(--z-index-popover)] outline-none"
        >
          <Dialog aria-label="Filter by channel" className={cn(menuPanel, "w-[240px]")}>
            {({ close }) => (
              <>
                {counts.channels.length === 0 ? (
                  <p className="px-2.5 py-2.5 text-[13px] text-co-muted">No channels chosen yet</p>
                ) : null}
                {counts.channels.map(({ platform, count }) => (
                  <MenuRow
                    key={platform}
                    active={value.channel === platform}
                    onPress={() => {
                      onChange({ ...value, channel: value.channel === platform ? null : (platform as CalendarPlatform) });
                      close();
                    }}
                    lead={<PlatformGlyph platform={platform} className="size-3.5 text-co-text2" />}
                    label={CHANNEL_NAME[platform]}
                    count={count}
                  />
                ))}
                {value.channel ? (
                  <MenuRow
                    active={false}
                    onPress={() => {
                      onChange({ ...value, channel: null });
                      close();
                    }}
                    label="Every channel"
                  />
                ) : null}
              </>
            )}
          </Dialog>
        </Popover>
      </DialogTrigger>
      <DialogTrigger>
        <FilterButton label={pillar?.name ?? "Pillars"} active={!!value.pillarId} menu />
        <Popover
          placement="bottom end"
          offset={6}
          UNSTABLE_portalContainer={portal ?? undefined}
          className="co-cal-peek z-[var(--z-index-popover)] outline-none"
        >
          <Dialog aria-label="Filter by pillar" className={cn(menuPanel, "w-[300px]")}>
            {({ close }) => (
              <>
                {/* Calendar / Pillar menu: the month's mix as one bar, then each pillar. */}
                {total > 0 ? (
                  <div className="flex flex-col gap-2 border-b border-co-line px-2.5 pb-3 pt-2.5">
                    <p className="text-[12.5px] text-co-muted">
                      {monthName ? `${monthName} by pillar · ` : "By pillar · "}
                      {total} {total === 1 ? "post" : "posts"}
                    </p>
                    <span className="flex h-1.5 w-full gap-0.5" aria-hidden="true">
                      {counts.pillars.map((entry) => (
                        <span
                          key={entry.id}
                          className="h-full"
                          style={{ flexGrow: entry.count, background: pillarHue(entry.index) ?? "var(--co-line2)" }}
                        />
                      ))}
                    </span>
                  </div>
                ) : (
                  <p className="px-2.5 py-2.5 text-[13px] text-co-muted">No pillars on this month&rsquo;s posts</p>
                )}
                {counts.pillars.map((entry) => (
                  <MenuRow
                    key={entry.id}
                    active={value.pillarId === entry.id}
                    onPress={() => {
                      onChange({ ...value, pillarId: value.pillarId === entry.id ? null : entry.id });
                      close();
                    }}
                    lead={
                      <span
                        aria-hidden="true"
                        className="size-2 shrink-0 rounded-full"
                        style={{ background: pillarHue(entry.index) ?? "var(--co-line2)" }}
                      />
                    }
                    label={entry.name}
                    count={entry.count}
                  />
                ))}
                {value.pillarId ? (
                  <MenuRow
                    active={false}
                    onPress={() => {
                      onChange({ ...value, pillarId: null });
                      close();
                    }}
                    label="Every pillar"
                  />
                ) : null}
              </>
            )}
          </Dialog>
        </Popover>
      </DialogTrigger>
    </div>
  );
}

/** A menu row: 36px, radius 10, 13/500; the chosen row takes the accent tint and a check. */
export function MenuRow({
  active,
  onPress,
  lead,
  label,
  count,
}: {
  active: boolean;
  onPress: () => void;
  lead?: ReactNode;
  label: string;
  count?: number;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onPress}
      className={cn(
        "flex h-9 w-full items-center gap-2.5 rounded-row px-2.5 text-left text-[13px] font-medium text-co-text outline-none hover:bg-co-hover focus-visible:bg-co-hover",
        active && "bg-co-accent-dim hover:bg-co-accent-dim",
      )}
    >
      {lead}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count !== undefined ? <span className="text-[12px] font-normal tabular-nums text-co-muted">{count}</span> : null}
      {active ? <Check className="size-3 text-co-accent" strokeWidth={2.25} aria-hidden="true" /> : null}
    </button>
  );
}

export function SettingsMenu({
  items,
  triggerClassName,
}: {
  items: { label: string; onPress: () => void }[];
  /** The phone's top lane wants a 44px target. */
  triggerClassName?: string;
}) {
  const portal = useCoShellPortal();
  return (
    <DialogTrigger>
      <CalendarTip label="Calendar settings">
        <AriaButton aria-label="Calendar settings" className={cn(glassCircle, triggerClassName)}>
          <Settings2 className={triggerClassName ? "size-5" : "size-4"} aria-hidden="true" />
        </AriaButton>
      </CalendarTip>
      <Popover
        placement="bottom end"
        offset={6}
        UNSTABLE_portalContainer={portal ?? undefined}
        className="co-cal-peek z-[var(--z-index-popover)] outline-none"
      >
        <Dialog aria-label="Calendar settings" className={cn(menuPanel, "w-[240px]")}>
          {({ close }) =>
            items.map((item) => (
              <MenuRow
                key={item.label}
                active={false}
                onPress={() => {
                  close();
                  item.onPress();
                }}
                label={item.label}
              />
            ))
          }
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

/** Text action (library): accent ink 13/500, no box. */
const textAction =
  "inline-flex h-8 items-center gap-1.5 px-1 text-[13px] font-medium text-co-accent-ink outline-none hover:underline focus-visible:outline-2 focus-visible:outline-co-accent";

/**
 * The month picker from the title (Calendar / Month picker · Popover): the open
 * month's calendar year, January to December, each a Month cell with its state
 * line, in floating chrome.
 */
function MonthPicker({
  picker,
  anchorRef,
  compact = false,
  tablet = false,
}: {
  picker: MonthPickerData;
  anchorRef: React.RefObject<HTMLDivElement | null>;
  /** The scrolled row: an 18px chevron. */
  compact?: boolean;
  /** The tablet header: a 20px chevron. */
  tablet?: boolean;
}) {
  const portal = useCoShellPortal();
  const [open, setOpen] = useState(false);
  // The window is a calendar year: January of the open month's year.
  const [start, setStart] = useState(() => ({ year: picker.year, month: 1 }));
  const today = new Date();
  const todayYear = today.getUTCFullYear();
  const todayMonth = today.getUTCMonth() + 1;
  const months = Array.from({ length: 12 }, (_, index) => addMonths(start.year, start.month, index));
  return (
    <DialogTrigger
      isOpen={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setStart({ year: picker.year, month: 1 });
      }}
    >
      <AriaButton
        aria-label="Choose a month"
        className={cn(
          "grid shrink-0 place-items-center text-co-text2 outline-none hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent",
          compact ? "size-[18px]" : tablet ? "size-5" : "size-[22px]",
        )}
      >
        <ChevronDown
          className={cn(compact ? "size-[18px]" : tablet ? "size-5" : "size-[22px]", "transition-transform", open && "rotate-180")}
          aria-hidden="true"
        />
      </AriaButton>
      <Popover
        triggerRef={anchorRef}
        placement="bottom start"
        offset={compact ? 10 : 8}
        UNSTABLE_portalContainer={portal ?? undefined}
        className="co-cal-peek z-[var(--z-index-popover)] outline-none"
      >
        <Dialog
          aria-label="Choose a month"
          className="co-glass-chrome relative flex w-[480px] max-w-[calc(100vw-2rem)] flex-col rounded-card outline-none"
        >
          <div className="flex items-center gap-2 px-4 py-3.5">
            <span className="text-[13px] font-semibold text-co-text">
              {start.year}
            </span>
            <span className="flex-1" />
            {picker.onSeeYear ? (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  picker.onSeeYear?.();
                }}
                className={textAction}
              >
                <ArrowUpRight className="size-3.5 text-co-muted" aria-hidden="true" />
                See year
              </button>
            ) : null}
            <button
              type="button"
              aria-label="Previous year"
              onClick={() => setStart((s) => addMonths(s.year, s.month, -12))}
              className={glassCircle}
            >
              <ChevronLeft className="size-[15px]" aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label="Next year"
              onClick={() => setStart((s) => addMonths(s.year, s.month, 12))}
              className={glassCircle}
            >
              <ChevronRight className="size-[15px]" aria-hidden="true" />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2 px-4">
            {months.map(({ year, month }) => {
              const isOpen = picker.markOpen !== false && year === picker.year && month === picker.month;
              const isNow = year === todayYear && month === todayMonth;
              const past = year < todayYear || (year === todayYear && month < todayMonth);
              const name = MONTH_NAMES[month - 1];
              const line = picker.monthLine?.(year, month) ?? fallbackLine(picker, year, month, isNow);
              return (
                <button
                  key={`${year}-${month}`}
                  type="button"
                  aria-current={isOpen ? "date" : undefined}
                  onClick={() => {
                    setOpen(false);
                    picker.onPick(year, month);
                  }}
                  className={cn(
                    // Calendar / Month cell: 62 high, radius 10; the open month in the lens with an accent line.
                    "flex h-[62px] min-w-0 flex-col justify-center gap-1 rounded-row px-3 text-left outline-none",
                    "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-co-accent",
                    isOpen ? "bg-[var(--co-lens)] shadow-[inset_0_0_0_1px_var(--co-accent)]" : "hover:bg-co-hover",
                  )}
                >
                  <span className={cn("truncate text-[13px] font-medium", past && !isNow ? "text-co-text2" : "text-co-text")}>
                    {name}
                  </span>
                  {line ? (
                    <span className="truncate text-[11.5px]" style={{ color: LINE_INK[line.tone] }}>
                      {line.text}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <div className="flex items-center px-4 py-3.5">
            <span className="text-[12px] text-co-muted">Pick a month to open it.</span>
            <span className="flex-1" />
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                picker.onPick(todayYear, todayMonth);
              }}
              className={textAction}
            >
              Today
            </button>
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

const LINE_INK: Record<MonthLine["tone"], string> = {
  accent: "var(--co-accent-ink)",
  alert: "var(--co-alert-ink)",
  muted: "var(--co-muted)",
};

export function addMonths(year: number, month: number, delta: number): { year: number; month: number } {
  const index = year * 12 + (month - 1) + delta;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

/** Without a summary per month: this month, and whether a month has posts. */
function fallbackLine(picker: MonthPickerData, year: number, month: number, isNow: boolean): MonthLine {
  const hasPosts = picker.monthsWithPosts.has(`${year}-${String(month).padStart(2, "0")}`);
  if (isNow) return { text: hasPosts ? "This month" : "This month · not planned", tone: "accent" };
  return { text: hasPosts ? "Has posts" : "Not planned", tone: "muted" };
}
